/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="../../components/models/meevo.model.custom.ts" />

module Meevo.Plugins.Reports {

    import IReportGeneralInfo = Meevo.Model.IReportGeneral;
    import IReportControl = Meevo.Model.IReportControl;
    import SecurityAccessEnum = Meevo.Model.SecurityAccessEnum;
    import ObjectStateEnum = Meevo.Model.ObjectStateEnum;
    import PageStateEnum = Meevo.Model.PageStateEnum;
    import ReportAvailabilityEnum = Meevo.Model.ReportAvailabilityEnum;
    import SecurityParts = Meevo.Components.Common.SecurityParts;
    import SecurityKeys = Meevo.Components.Common.SecurityKeys;
    import ReportRenderingEnum = Meevo.Model.ReportRenderingEnum;

    const ALL_REPORT_CATEGORY: IReportCategory = {
        displayName: 'All Reports',
        desc: 'All Reports',
        id: null,
    };

    const MIN_LENGTH_FOR_SEARCH = 3;

    export interface IReportsContext {
        showInputForm: boolean;
        reportCategories: Array<IReportCategory>;
        selectedCategory: IReportCategory;
        reportList: Array<IReportGeneralInfo>;
        selectedReport: IReportGeneralInfo;
        reportControls: Array<IReportControl>;
        reportAuditEntity: Guid;
        reportModel: Object;
        reportModelForSubmit: Object;
        showMenuType: Number;
        searchFilter: string;
        reportUrl: string;
        hierarchyForm: ng.IFormController;
        validationJson: any;

        goBack(): void;

        activate(): void;

        loadReportCategories(showBusy?: boolean): ng.IPromise<IReportCategory[]>;

        loadReportListByCategoryId(showBusy?: boolean): ng.IPromise<Array<IReportGeneralInfo>>;

        loadReportInputTemplate(report: IReportGeneralInfo, showBusy?: boolean, reportContext?: any): ng.IPromise<any>;

        loadReportFromParams(params: any): void;

        loadReportCategoryFromParams(params: any): void;

        refreshForAuthenticatedUser(showBusy: boolean): ng.IPromise<boolean>;

        reportFormatList: any;

        reportDeliveryFileFormatEnum: any;
    }

    export class ReportsContext implements IReportsContext {
        showInputForm: boolean;
        reportList: Array<IReportGeneralInfo>;
        selectedReport: IReportGeneralInfo;
        reportCategories: Array<IReportCategory>;
        selectedCategory: IReportCategory;
        reportControls: Array<IReportControl>;
        reportAuditEntity: Guid;
        reportModel: Object = {};
        reportModelForSubmit: Object = {};
        reportUrl: string;
        hierarchyForm: ng.IFormController;
        validationJson: any = {};
        showMenuType: PageStateEnum;
        startDayOfWeek?: Number;
        runPermission: SecurityAccessEnum = SecurityAccessEnum.Hide;
        isBackArrowVisible: boolean;
        searchFilter: string;
        isEmptyReportList: boolean = false;
        reportFormatList: any;
        reportDeliveryFileFormatEnum: any;
        analyticalReportAccess: any;
        kendoReportUrl: string;
        labelType: DP005LabelTypeEnum;

        get searchBtnDisabled(): boolean {
            return !this.searchFilter || this.searchFilter.length < MIN_LENGTH_FOR_SEARCH;
        }

        get isKendoReportLoaded(): boolean {
            return this.showMenuType === PageStateEnum.Report && this.selectedReport.renderingEnum === ReportRenderingEnum.Kendo;
        }

        constructor(protected appSettings: Meevo.Components.Common.IAppSettings,
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected modalService: Meevo.Components.Modal.IModalService,
            protected identityService: Meevo.Components.Common.IIdentityService,
            protected loggingService: Meevo.Components.Logging.ILoggingService,
            protected busyService: Meevo.Components.Busy.IBusyService,
            protected securityService: Meevo.Components.Common.ISecurityService,
            protected $scope: ng.IScope,
            protected $sce: angular.ISCEService,
            protected $q: angular.IQService,
            protected reportsHelper: IReportsHelper,
            protected $translate: angular.translate.ITranslateService) {
            this.showMenuType = PageStateEnum.CategoryList;
            this.reportFormatList = [
                { id: "PDF", displayName: "PDF" },
                { id: "HTML", displayName: "HTML" },
                { id: "XLSX", displayName: "XLSX" }
            ];
            // Get Module Access for Analytical Reports
            this.securityService.checkModuleAccess(SecurityKeys.REPORTS_ANALYTICAL + SecurityParts.KEY_PART_READ)
                .then(access => {
                    this.analyticalReportAccess = access;
                });
        }

        /**
         * Initial function of context
         * */
        activate(): void {
            this.loadReportCategories()
                .then(() => {
                    // automatically select report
                    this.selectedCategory = this.reportCategories[0];
                    this.loadReportListByCategoryId();
                });

        }

        /**
         * Load list of report categories
         * @return Promise
         * */
        loadReportCategories(showBusy: boolean = true): ng.IPromise<IReportCategory[]> {
            if (showBusy) {
                this.busyService.start();
            }

            return this.dataService.getListPaged(this.appSettings.API_ENDPOINTS.reportCategories, {
                criteria: {
                    isContainsReports: true,
                    objectStateEnum: ObjectStateEnum.Active
                }
            })
                .then((result: IReportCategory[]) => this.checkSecurityReport(result))
                .then((result: IReportCategory[]) => {
                    let arrayResult: Array<IReportCategory> = [];
                    this.reportCategories = [{ ...ALL_REPORT_CATEGORY }, ...result];
                    if (showBusy) {
                        this.busyService.stop();
                    }

                    return this.dataService.customMethodData('api/Tenant/BusinessInfo/Settings', 'get', {})
                        .then((data) => {
                            this.startDayOfWeek = data.data.appointmentBookSettings.startAppointmentBookDayOn;

                            return arrayResult;
                        });

                });
        }

        /**
         * Check reports by security key
         */
        checkSecurityReport(reports: IReportCategory[]): ng.IPromise<IReportCategory[]> {
            let promises = reports.map((report) => {
                const securityName: string = this.reportsHelper.reportNameToSecurityKey(report.displayName);

                return this.securityService.checkSecurityKey('REPORTS:' + securityName + SecurityParts.KEY_PART_READ)
                    .catch(() => SecurityAccessEnum.Hide);
            });

            return this.$q.all(promises).then(() => {
                let result: IReportCategory[] = [];
                reports.forEach((item) => {
                    const securityName: string = this.reportsHelper.reportNameToSecurityKey(item.displayName);
                    if (this.securityService.hasSecurity('REPORTS:' + securityName + SecurityParts.KEY_PART_READ)) {
                        result.push(item);
                    }
                });

                return result;
            });

        }

        /**
         * Load list of reports that match for provided category id
         * */
        loadReportListByCategoryId(showBusy: boolean = true, isReportWithParams: boolean = false): ng.IPromise<Array<IReportGeneralInfo>> {
            if (showBusy) {
                this.busyService.start();
            }

            if (!isReportWithParams) {
                this.setAnalyticsTrackingUrl(this.selectedCategory.displayName, undefined);
            }

            return this.dataService.getListPaged(this.appSettings.API_ENDPOINTS.report, {
                criteria: {
                    filter: this.searchFilter || '',
                    categoryId: this.selectedCategory.id,
                    objectStateEnum: ObjectStateEnum.Active
                }
            })
                .then((result: Array<IReportGeneralInfo> | any) => {
                    let reports: Array<IReportGeneralInfo> = [];

                    this.isEmptyReportList = result.length === 0;

                    let promises = result
                        .filter((report) => report.renderingEnum !== ReportRenderingEnum.QuickSight)
                        .filter((report) => {
                            // filter analytical reports when module is not enabled
                            if (report.securityKey.includes(SecurityKeys.REPORTS_ANALYTICAL) && this.analyticalReportAccess != Model.SecurityAccessEnum.Grant) {
                                return false
                            }
                            if (report.reportAvailability === ReportAvailabilityEnum.Both) {
                                return true;
                            }
                            if (this.identityService.inCentralOfficeMode()) {
                                return report.reportAvailability === ReportAvailabilityEnum.GlobalCentralOffice;
                            } else {
                                return report.reportAvailability === ReportAvailabilityEnum.Location;
                            }
                        })
                            .map((report) => {
                                // Translate report display name if applicable
                                var translatedDisplayName = this.$translate.instant(`LABEL_${report.shortCut.toUpperCase()}_DISPLAY_NAME`);
                                if (translatedDisplayName && translatedDisplayName.indexOf('LABEL_') === -1) {
                                    report.displayName = translatedDisplayName;
                                }

                                if (report.shortCut === 'DP005') {
                                    switch (this.appSettings.APP_BUSINFOSETTINGS.culturalSettings.countryEnum) {
                                        case Model.CountryEnum.UnitedKingdom:
                                        case Model.CountryEnum.Switzerland:
                                        case Model.CountryEnum.Spain:
                                        case Model.CountryEnum.Ireland:
                                            report.description = this.$translate.instant("LABEL_DP005_DESCRIPTION_L4736_LABELS");
                                            this.labelType = DP005LabelTypeEnum.avery4736;
                                            break;
                                        default:
                                            report.description = this.$translate.instant("LABEL_DP005_DESCRIPTION_8167_LABELS");
                                            this.labelType = DP005LabelTypeEnum.avery8167;
                                            break;
                                    }
                                } else {
                                    // Translate report description if applicable
                                    var translatedDescription = this.$translate.instant(`LABEL_${report.shortCut.toUpperCase()}_DESCRIPTION`);
                                    if (translatedDescription && translatedDescription.indexOf('LABEL_') === -1) {
                                        report.description = translatedDescription;
                                    }
                                }
                                return report;
                            })
                            .map((report) => this.securityService.checkSecurityKey(report.securityKey)
                                .then((permission: SecurityAccessEnum) => {
                                    report.permission = permission;

                                    if (permission !== SecurityAccessEnum.Hide) {
                                        reports.push(report);
                                    }
                                })
                        );

                    return this.$q.all(promises).then(() => {
                        this.reportList = reports.filter(report => report.id !== 224); // Hide Core Royalty from report screen
                        this.showMenuType = PageStateEnum.ReportList;
                        this.isBackArrowVisible = this.checkPageStatus();
                        if (showBusy) {
                            this.busyService.stop();
                        }

                        return result;
                    });
                });
        }

        /**
         * Get and render report with permissions
         * */
        loadReportInputTemplate(report: IReportGeneralInfo, showBusy: boolean = true, reportContext?: any, reportContextValues?: any): ng.IPromise<any> {

            this.showInputForm = true;
            this.selectedReport = report;
            this.reportModel = {};
            this.reportModelForSubmit = {};
            this.runPermission = report.permission;

            if (showBusy) {
                this.busyService.start();
            }

            if (report.renderingEnum === ReportRenderingEnum.Kendo) {
                // Kendo reports
                const domain = this.appSettings.MEEVO_MODULES_URL;
                const url = `${domain}/reports/${report.id}`;
                this.kendoReportUrl = this.$sce.trustAsResourceUrl(url);
                this.showMenuType = PageStateEnum.Report;
                this.isBackArrowVisible = this.checkPageStatus();
                this.setAnalyticsTrackingUrl(this.selectedCategory.displayName, report.shortCut);
                return this.$q.resolve();
            }

            this.reportDeliveryFileFormatEnum = this.reportFormatList[0].id;
            if (reportContextValues) {
                for (var key in reportContextValues) {
                    this.reportModel[key] = reportContextValues[key];
                }
            }

            if (this.selectedReport.shortCut === 'MA240') {
                // check if memberships exist to determine visibility of dropdown
                this.dataService.customMethodData(`${this.appSettings.API_ROOT}/membershiptype/Count/`, 'POST', {
                    criteria: {
                        includeActive: true,
                        includeInactive: true,
                        includeDeleted: true
                    }
                }).then(response => {
                    if (response.data === 0) {
                        this.reportModel['ClientMembershipFilter'] = 'hidden';
                    }
                });
            }

            if (this.selectedReport.shortCut === 'MA065') {
                this.dataService.customMethodData(`${this.appSettings.API_ROOT}/tax`, 'GET', null).then((response) => {
                    this.reportModel['IsTaxIncluded'] = response.data.isTaxIncludedInPrice;
                });
            }

            if (this.selectedReport.shortCut === 'DP005') {
                this.reportModel['LabelType'] = this.labelType;
            }

            if (reportContext) {
                this.reportControls = <Array<IReportControl>>reportContext.controls;
                this.reportAuditEntity = <Guid>reportContext.reportAuditEntity;
                this.showMenuType = PageStateEnum.Controls;
                this.isBackArrowVisible = this.checkPageStatus();
                if (this.reportControls.find(control => this.checkIfControlNeedsTimezone(control))) {
                    this.loadTimeZoneOffset();
                }
                if (reportContext.validationJSON) {
                    this.loadValidationJson(reportContext);
                } else {
                    this.validationJson = {};
                }

                this.setAnalyticsTrackingUrl(this.selectedCategory.displayName, reportContext.shortCut);

                if (showBusy) {
                    this.busyService.stop();
                }

                return this.$q.resolve();
            } else {
                return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/report/report/${report.id}`, 'GET', {})
                    .then((result: ng.IHttpResponseTransformer | any) => {
                        this.reportControls = <Array<IReportControl>>result.data.controls;
                        this.reportAuditEntity = <Guid>result.data.reportAuditEntity;
                        this.showMenuType = PageStateEnum.Controls;
                        this.isBackArrowVisible = this.checkPageStatus();
                        if (this.reportControls.find(control => this.checkIfControlNeedsTimezone(control))) {
                            this.loadTimeZoneOffset();
                        }
                        if (result.data.validationJSON) {
                            this.loadValidationJson(result.data);
                        } else {
                            this.validationJson = {};
                        }

                        this.setAnalyticsTrackingUrl(this.selectedCategory.displayName, report.shortCut);

                        if (showBusy) {
                            this.busyService.stop();
                        }
                    });
            }
        }

        checkIfControlNeedsTimezone(control: IReportControl) {
            switch (control.displayName) {
                case 'DateRange':
                case 'DisplayCreditCards':
                case 'MonthPicker':
                case 'MonthYearPicker':
                case 'SalesBreakdownBy':
                    return true;
            }
            return false;
        }

        loadTimeZoneOffset() {
            // add property so date control knows to watch for changes to it
            this.reportModel['timeZoneOffset'] = '';
            if (this.securityService.hasSecurity(Meevo.Components.Common.SecurityKeys.DATA_BUSINESSINFO_TAB_MAIN + Meevo.Components.Common.SecurityParts.KEY_PART_READ)) {
                this.dataService.customMethodData('api/tenant/businessinfo/', 'get', {}).then((data) => {
                    this.reportModel['timeZoneOffset'] = data.data['timeZoneOffset'];
                });
            }
        }

        loadValidationJson(response: any): void {
            this.validationJson = JSON.parse(response.validationJSON);
            if (this.validationJson.DateBack) {
                this.validationJson.DateBack = {
                    from: moment(new Date()).subtract(this.validationJson.DateBack[0], 'd').toDate(),
                    to: moment(new Date()).subtract(this.validationJson.DateBack[1], 'd').toDate()
                };
            }
            if (this.validationJson.MaxDateCalendar) {
                this.validationJson.MaxDateCalendar = moment(new Date()).subtract(this.validationJson.MaxDateCalendar, 'd').toDate();
            }
            if (this.validationJson.MinDateCalendar) {
                this.validationJson.MinDateCalendar = moment(new Date()).add(this.validationJson.MinDateCalendar, 'd').toDate();
            }
            if (this.validationJson.ShowIfChecked) {
                Object.keys(this.validationJson.ShowIfChecked).forEach((key) => {
                    if (typeof this.validationJson.ShowIfChecked[key] === 'string') {
                        this.validationJson.ShowIfChecked[key] = [this.validationJson.ShowIfChecked[key]];
                    }
                });
            }
        }

        loadReportFromParams(params: any): void {
            if (
                this.showMenuType === PageStateEnum.Report &&
                this.selectedReport &&
                this.selectedReport.id === params.reportId &&
                this.selectedReport.renderingEnum === ReportRenderingEnum.Kendo
            ) {
                // it's a Kendo report and it's already loaded, pass
                return;
            }

            this.busyService.start();
            this.loadReportCategories(false).then(() => {
                this.selectedCategory = _.findWhere(this.reportCategories, { id: params.reportContext.reportCatId });
                if (this.selectedCategory) {
                    this.loadReportListByCategoryId(false, true).then(() => {
                        this.selectedReport = _.findWhere(this.reportList, { id: params.reportContext.id });
                        if (this.selectedReport) {
                            // Request to reinitialize report template if no report controls are present in report context
                            if (!params.reportContext.controls)
                                params.reportContext = undefined;

                            if (this.selectedReport.shortCut === 'DC080' && params.reportContextData) {
                                this.selectedReport.customData = params.reportContextData;
                            }

                            if (this.selectedReport.shortCut === 'DP005' && (params.reportContextData || params.reportContextValues)) {//grab displayName from somwhere else, create func
                                this.selectedReport.customData = params.reportContextData;
                                if (this.selectedReport.customData && this.selectedReport.customData.transferNumber) {
                                    this.selectedReport.displayName = this.selectedReport.displayName + " " + this.$translate.instant("LABEL_FOR_TRANSFER") + " " + this.selectedReport.customData.transferNumber
                                }
                                else if (params.reportContextValues && params.reportContextValues.purchaseOrderNumber) {
                                    this.selectedReport.displayName = this.selectedReport.displayName + " " + this.$translate.instant("LABEL_FOR_PO") + " " + params.reportContextValues.purchaseOrderNumber;
                                }
                                else if (params.reportContextValues) {
                                    this.selectedReport.displayName = this.selectedReport.displayName + " " + this.$translate.instant("LABEL_FOR") + " " + params.reportContextValues.displayName;
                                }
                            }
                            if (this.selectedReport.permission === SecurityAccessEnum.Deny) {
                                this.busyService.stop();
                                this.modalService.warning('LABEL_REPORT_ACCESS_DENIED', Components.Modal.ModalTranslationModes.Translate);
                                return;
                            }
                            this.loadReportInputTemplate(this.selectedReport, false, params.reportContext, params.reportContextValues)
                                .then(() => {
                                    this.busyService.stop();
                                });
                        } else {
                            this.busyService.stop();
                        }
                    });
                } else {
                    this.busyService.stop();
                }
            });
        }

        loadReportCategoryFromParams(params: any): void {
            this.busyService.start();
            this.loadReportCategories(false)
                .then(() => {
                    this.selectedCategory = _.findWhere(this.reportCategories, { id: params.reportContext.reportCatId });
                    if (this.selectedCategory) {
                        return this.loadReportListByCategoryId(false)
                    }
                })
                .then(() => this.busyService.stop());
        }

        /**
         * Clear the previous user's report state and reload the tiles with the
         * newly authenticated user's security access.
         */
        refreshForAuthenticatedUser(showBusy: boolean): ng.IPromise<boolean> {
            this.showInputForm = false;
            this.showMenuType = PageStateEnum.CategoryList;
            this.isBackArrowVisible = false;
            this.selectedReport = undefined;
            this.selectedCategory = undefined;
            this.searchFilter = '';
            this.reportCategories = [];
            this.reportList = [];
            this.reportControls = [];
            this.reportModel = {};
            this.reportModelForSubmit = {};
            this.validationJson = {};
            this.kendoReportUrl = undefined;
            this.runPermission = SecurityAccessEnum.Hide;
            this.isEmptyReportList = false;

            if (showBusy) {
                this.busyService.start();
            }

            return this.securityService.refreshSecurityKeys(true)
                .then(() => this.securityService.checkModuleAccess(SecurityKeys.REPORTS_ANALYTICAL + SecurityParts.KEY_PART_READ))
                .then((access) => {
                    this.analyticalReportAccess = access;
                    return this.loadReportCategories(false);
                })
                .then(() => {
                    this.selectedCategory = this.reportCategories[0];
                    return this.loadReportListByCategoryId(false);
                })
                .then(() => this.reportList.length > 0)
                .catch((error) => {
                    this.loggingService.error('Unable to reload reports after authentication change.', error);
                    return false;
                })
                .finally(() => {
                    if (showBusy) {
                        this.busyService.stop();
                    }
                });
        }

        /**
         * Check current/target permission
         * @param type - permission type
         * @param targetPermission - check target
         * @returns result
         * */
        checkSecurityKey(type: string = 'Grant', targetPermission?: any): boolean {
            let target = targetPermission ? targetPermission : this.runPermission;

            return target === SecurityAccessEnum[type];
        }

        /**
         * Navigate to previous screen
         * */
        goBack() {
            if (this.selectedReport.renderingEnum === ReportRenderingEnum.Kendo && this.showMenuType === PageStateEnum.Report) {
                this.showMenuType = PageStateEnum.ReportList;
                this.isBackArrowVisible = this.checkPageStatus();
                this.setAnalyticsTrackingUrl(this.selectedCategory.displayName, undefined);
                return;
            }

            switch (this.showMenuType) {
                case PageStateEnum.CategoryList:
                    break;
                case PageStateEnum.ReportList:
                    this.showMenuType = PageStateEnum.CategoryList;
                    this.setAnalyticsTrackingUrl(undefined, undefined);
                    break;
                case PageStateEnum.Controls:
                    this.showMenuType = Meevo.Model.PageStateEnum.ReportList;
                    this.setAnalyticsTrackingUrl(this.selectedCategory.displayName, undefined);
                    break;
                case PageStateEnum.Report:
                    this.showMenuType = Meevo.Model.PageStateEnum.Controls;
                    break;
                default:
                    break;
            }
            this.isBackArrowVisible = this.checkPageStatus();
        }

        checkPageStatus(): boolean {
            return this.showMenuType !== PageStateEnum.CategoryList && this.showMenuType !== PageStateEnum.ReportList;
        }

        get pageState(): any {
            return PageStateEnum;
        }

        search(event?: KeyboardEvent) {
            if (event) {
                event.preventDefault();
            }

            if (this.searchBtnDisabled) {
                return;
            }

            const allCategory = this.reportCategories.find((category) => category.id === ALL_REPORT_CATEGORY.id);
            if (!!allCategory) {
                this.selectedCategory = allCategory;
            }

            this.loadReportListByCategoryId(true);
        }

        kendoFrameLoaded = () => {
            this.busyService.stop(true);
        }

        private setAnalyticsTrackingUrl(category: string, shortCut: string) {
            const pendo = window['pendo'];
            if (!pendo || !pendo.location) return;

            let url = `${window.location.origin}/reports`;
            if (category) url += `/${category.replace(/[^a-zA-Z]+/g, '')}`;
            if (shortCut) url += `/${shortCut}`;
            pendo.location.setUrl(url);
            console.log(`PENDO: ${url}`);
        }
    }
}
